import { fetch, getApiOrigin } from '@lib/fetch';
import { getDefinedUrlSearchParams } from '@lib/util';

export function userCompaniesGetAllByAdmin(id) {
  return fetch(`/v3/admin/users/${id}/companies/list`, {
    method: 'GET',
  });
}

export function productsGet(queryParams) {
  return fetch(`/v3/products?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function productListGet(queryParams) {
  return fetch(`/v3/products/list?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function productsCreatedForMeGet(queryParams) {
  return fetch(
    `/v3/products/created-for-me?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function productWithComponentsCreate(body) {
  return fetch('/v3/products/create-with-components', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function bulkCreateProducts(body) {
  return fetch('/v3/products/bulk-upload-nuvo', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function productGet(id) {
  return fetch(`/v3/products/${id}`, {
    method: 'GET',
  });
}

export function productEcoFootprintGet(id) {
  return fetch(`/v3/products/${id}/eco`, {
    method: 'GET',
  });
}

export function requestMissingClaimInformation(claimId, body) {
  return fetch(`/v3/claims/${claimId}/request-missing-information`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// Reads, writes and access requests for company documents are served by the
// modules-based `/api/company-documents` routes (see `./backend`), and the
// certificate list by `GET /api/claims`. Re-exported under the legacy names so
// the existing callers keep working unchanged: `claimsGet` is still used by the
// company card and the supply-chain node drawer, which read only its `data`.
export {
  claimsList as claimsGet,
  companyDocumentBulkCreate,
  companyDocumentGet,
  companyDocumentMakePublic,
  companyDocumentPatch,
  companyDocumentShare,
  companyDocumentsCancelRequest,
  companyDocumentsGet,
  companyDocumentsRequest,
} from './backend';

export function companyDocumentsDownload(body) {
  return fetch('/v3/company-documents/download', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function companyDocumentsListShared(queryParams) {
  return fetch(
    [
      `${getApiOrigin()}/api/company-documents/shared?${getDefinedUrlSearchParams(queryParams)}`,
    ],
    {
      method: 'GET',
    },
  );
}

export function cascadeTracingAttachLinkDocumentsV1(body) {
  return fetch(
    [
      `${getApiOrigin()}/api/cascade-tracing/transactions/attach-link-documents`,
    ],
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

export function auditCreate(body) {
  return fetch('/v3/audits', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function auditsUpdate(id, body) {
  return fetch(`/v3/audits/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function getCompliance(type) {
  return fetch(
    `${type ? `/v3/analytics/compliance/${type}` : '/analytics/compliance/'}`,
    {
      method: 'GET',
    },
  );
}

export function auditPatch(id, body) {
  return fetch(`/v3/audits/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function auditBulkDelete(body) {
  return fetch('/v3/audits/bulk-delete', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

// DELTA-2545 — the legacy `/v3/audits` sharing endpoints are gone, and with them
// `auditBulkArchive`, `auditMakePublic`, `auditShare`, `auditRequest` and
// `auditCancelRequest`. The first four already had no caller: every view imports
// the identically-named function from `./audits-service`, which is on `/v1`.
// `auditMakePublic` and `auditCancelRequest` now live there too.

export function productStoryGet(shop, sku) {
  return fetch(`/v3/products/story?sku=${sku}`, {
    method: 'GET',
    headers: {
      shopid: shop,
    },
  });
}

export function styleStoryGet(shop, sku) {
  return fetch(`/v3/styles/story?sku=${sku}`, {
    method: 'GET',
    headers: {
      shopid: shop,
    },
  });
}

export function productUpdateQrCodeTag(id) {
  return fetch(`/v3/products/${id}/qr-code-tag`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function cascadeTracingPurchaseOrderGet(purchaseOrderId) {
  return fetch(`/v3/cascade-tracing/orders/purchases/${purchaseOrderId}`, {
    method: 'GET',
  });
}

export function cascadeTracingTracingGetByPurchaseOrderId(id) {
  return fetch(`/v3/cascade-tracing/orders/trace/purchases/${id}`, {
    method: 'GET',
  });
}

export function cascadeTracingProductTracingGetByPurchaseOrderId(id) {
  return fetch(`/v3/cascade-tracing/orders/product-trace/purchases/${id}`, {
    method: 'GET',
  });
}

export function cascadeTracingSalesOrdersGet(queryParams) {
  return fetch(
    `/v3/cascade-tracing/orders/sales?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function cascadeTracingSalesOrderGet(salesOrderId) {
  return fetch(`/v3/cascade-tracing/orders/sales/${salesOrderId}`, {
    method: 'GET',
  });
}

export function cascadeTracingProductTracingGetBySalesOrderId(id) {
  return fetch(`/v3/cascade-tracing/orders/product-trace/sales/${id}`, {
    method: 'GET',
  });
}

export function cascadeTracingProductTracingLineLinkGet(id, queryParams) {
  return fetch(
    `/v3/cascade-tracing/transactions/product-line-link/${id}?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function cascadeTracingProductTracingNodeProductTransformationGet(
  id,
  queryParams,
) {
  return fetch(
    `/v3/cascade-tracing/transactions/product-transformations/${id}?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function cascadeTracingTransactionLayerGet(queryParams) {
  return fetch(
    `/v3/cascade-tracing/transactions/layer?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function cascadeTracingAttachLinkDocuments(body) {
  return fetch(
    [
      `${getApiOrigin()}/api/cascade-tracing/transactions/attach-link-documents`,
    ],
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

export function cascadeTracingAttachNodeDocument(body) {
  return fetch(
    [
      `${getApiOrigin()}/api/cascade-tracing/transactions/attach-node-documents`,
    ],
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

export function cascadeTracingRemoveLinkDocument(body) {
  return fetch('/v3/cascade-tracing/transactions/remove-link-document', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function cascadeTracingRemoveLinkDocuments(body) {
  return fetch(
    `${getApiOrigin()}/api/cascade-tracing/transactions/remove-link-documents`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

export function cascadeTracingAttachmentsGetBySalesOrderId(id) {
  return fetch(
    [`${getApiOrigin()}/api/cascade-tracing/orders/sales/${id}/attachments`],
    {
      method: 'GET',
    },
  );
}

export function cascadeTracingAttachmentsGetByPurchaseOrderId(id) {
  return fetch(
    [
      `${getApiOrigin()}/api/cascade-tracing/orders/purchases/${id}/attachments`,
    ],
    {
      method: 'GET',
    },
  );
}

export function productCategoriesFieldValuesGet(productId, queryParams) {
  return fetch(
    `/v3/products/${productId}/category-fields-values?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function productLinking(body) {
  return fetch('/v3/products/link-product', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function validateCertificate(data) {
  return fetch('/v3/certifications/validate', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function productCertificationsGet(id, queryParams) {
  return fetch(
    `/v3/products/${id}/certifications?${getDefinedUrlSearchParams(queryParams)}`,
    {},
  );
}

export function attachmentTemplateByAdmin(body) {
  return fetch([`${getApiOrigin()}/api/admin/attachment-templates`], {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function editAttachmentTemplateByAdmin(id, body) {
  return fetch([`${getApiOrigin()}/api/admin/attachment-templates/${id}`], {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function attachmentTemplatesGetByAdmin(queryParams) {
  return fetch(
    [
      `${getApiOrigin()}/api/admin/attachment-templates?${getDefinedUrlSearchParams(queryParams)}`,
    ],
    {
      method: 'GET',
    },
  );
}

export function attachmentTemplatesAggregateGet(queryParams) {
  return fetch(
    [
      `${getApiOrigin()}/api/attachment-templates/aggregate?${getDefinedUrlSearchParams(queryParams)}`,
    ],
    {
      method: 'GET',
    },
  );
}

export function attachmentTemplatesGet(queryParams) {
  return fetch(
    [
      `${getApiOrigin()}/api/attachment-templates?${getDefinedUrlSearchParams(queryParams)}`,
    ],
    {
      method: 'GET',
    },
  );
}

export function attachmentTemplatesPost(body) {
  return fetch([`${getApiOrigin()}/api/attachment-templates`], {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function attachmentTemplateGetByAdmin(id) {
  return fetch(
    [`${getApiOrigin()}/api/admin/attachment-templates/${id}/details`],
    {
      method: 'GET',
    },
  );
}

export function attachmentTemplateGet(id) {
  return fetch([`${getApiOrigin()}/api/attachment-templates/${id}/details`], {
    method: 'GET',
  });
}

export function attachmentTemplatePut(id, body) {
  return fetch([`${getApiOrigin()}/api/attachment-templates/${id}`], {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function attachmentTemplateArchive(id) {
  return fetch([`${getApiOrigin()}/api/attachment-templates/${id}/archive`], {
    method: 'POST',
  });
}

export function attachmentTemplateDefinedRulesGet(id, queryParams) {
  console.log(queryParams);
  return fetch(
    [
      `${getApiOrigin()}/api/attachment-templates/${id}/documents?${getDefinedUrlSearchParams(queryParams)}`,
    ],
    {
      method: 'GET',
    },
  );
}
export function attachmentTemplateUpdatePreference(
  attachmentTemplatePreferenceId,
) {
  return fetch([`${getApiOrigin()}/api/attachment-templates/preference`], {
    method: 'PUT',
    body: JSON.stringify({
      attachmentTemplateId: attachmentTemplatePreferenceId
        ? attachmentTemplatePreferenceId
        : null,
    }),
  });
}

export function getAllProductVariations() {
  return fetch('/v3/products/get-all-product-variations', {
    method: 'GET',
  });
}

function mapImpactAreaToLegacy(item) {
  return { ...item, hierarchyId: item.sectionId, description: item.name };
}

function mapImpactAreaListResponse(res) {
  if (!res.data) return res;
  return {
    ...res,
    data: { data: (res.data.data || []).map(mapImpactAreaToLegacy) },
  };
}

export function adminListAllCategories() {
  return fetch([backend.url, '/impact-areas'], { method: 'GET' }).then(
    mapImpactAreaListResponse,
  );
}

export function adminGetCategories(queryParams) {
  const params = {};
  if (queryParams?.filters) {
    try {
      const f = JSON.parse(queryParams.filters);
      if (f.parentHierarchyId) params.parentSectionId = f.parentHierarchyId;
    } catch {
      /* ignore invalid JSON */
    }
  }
  return fetch(
    [backend.url, `/impact-areas?${getDefinedUrlSearchParams(params)}`],
    { method: 'GET' },
  ).then(mapImpactAreaListResponse);
}

export function getCategories(queryParams) {
  return adminGetCategories(queryParams);
}

export function getCategory(id) {
  return fetch([backend.url, `/impact-areas/${id}`], { method: 'GET' }).then(
    res => ({
      ...res,
      data: res.data?.data ? mapImpactAreaToLegacy(res.data.data) : res.data,
    }),
  );
}

export function adminGetCategory(id) {
  return getCategory(id);
}

export function createFiberLot(body) {
  return fetch('/v3/fiber/lots', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getFiberLotsByProductId(id, queryParams) {
  return fetch(
    `/v3/fiber/lots/by-product/${id}?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function getFiberLots(queryParams) {
  return fetch(`/v3/fiber/lots?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function createFiberTransaction(body) {
  return fetch('/v3/fiber/transactions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fiberOutgoingTransactionGet(transactionId) {
  return fetch(`/v3/fiber/transactions/outgoings/${transactionId}`, {
    method: 'GET',
  });
}

export function fiberOutgoingTransactionsGet(queryParams) {
  return fetch(
    `/v3/fiber/transactions/outgoings?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function fiberIncomingTransactionGet(transactionId) {
  return fetch(`/v3/fiber/transactions/incomings/${transactionId}`, {
    method: 'GET',
  });
}

export function fiberIncomingTransactionsGet(queryParams) {
  return fetch(
    `/v3/fiber/transactions/incomings?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function fiberProductionProcessCreate(body) {
  return fetch('/v3/fiber/production-processes', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fiberProductionProcessesGet(queryParams) {
  return fetch(
    `/v3/fiber/production-processes?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function fiberProductionFactorsGet({
  productCategoryId,
  facilityProcessId,
}) {
  return fetch(
    `/v3/fiber/production-factors/${productCategoryId}?${getDefinedUrlSearchParams(
      {
        facilityProcessId,
      },
    )}`,
    {
      method: 'GET',
    },
  );
}

export function fiberProductionProcessGet(id) {
  return fetch(`/v3/fiber/production-processes/${id}`, {
    method: 'GET',
  });
}

export function fiberProductionProcessUpdate(id, body) {
  return fetch(`/v3/fiber/production-processes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function fiberProductionProcessPatch(id, body) {
  return fetch(`/v3/fiber/production-processes/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function fiberProductionProcessCocModelsGet() {
  return fetch('/v3/fiber/production-processes/coc-models', {
    method: 'GET',
  });
}

export function fiberTransformationCreate(body) {
  return fetch('/v3/fiber/production-processes/transformations', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fiberTransformationsGet(queryParams) {
  return fetch(
    `/v3/fiber/production-processes/transformations?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function fiberTransformationGet(id) {
  return fetch(`/v3/fiber/production-processes/transformations/${id}`, {
    method: 'GET',
  });
}

export function fiberProductionProcessTransformationDocumentsAttach(body) {
  return fetch(
    '/v3/fiber/production-processes/attach-transformation-documents',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

export function fiberProductionProcessTransformationDocumentsRemove(body) {
  return fetch(
    '/v3/fiber/production-processes/remove-transformation-documents',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

export function fiberProductsStocksGet(queryParams) {
  return fetch(
    `/v3/fiber/stocks/products?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function fiberArchivedLotsGet(queryParams) {
  return fetch(
    `/v3/fiber/lots/archived?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function fiberComponentsStocksGet(queryParams) {
  return fetch(
    `/v3/fiber/stocks/components?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function fiberProductStockLotsGet(id, queryParams) {
  return fetch(
    `/v3/fiber/stocks/lots/${id}?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function fiberProductionProcessProductsByCocModelGet({
  fiberCocModelId,
  fiberProductionProcessId,
  isStockRequired,
  isOptional,
  isOutput,
}) {
  return fetch(
    `/v3/fiber/production-processes/products/${fiberCocModelId}?${getDefinedUrlSearchParams(
      {
        fiberProductionProcessId,
        isStockRequired,
        isOptional,
        isOutput,
      },
    )}`,
    {
      method: 'GET',
    },
  );
}

export function fiberStocksAvailableProducts({ isStockRequired }) {
  return fetch(
    `/v3/fiber/stocks/available-products?${getDefinedUrlSearchParams({
      isStockRequired,
    })}`,
    {
      method: 'GET',
    },
  );
}

export function fiberMatchReceivingProducts(incomingProductId) {
  return fetch(`/v3/fiber/transactions/matched-products/${incomingProductId}`, {
    method: 'GET',
  });
}

export function fiberTransactionsDocumentsAttach(body) {
  return fetch('/v3/fiber/transactions/attach-documents', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fiberTransactionsDocumentsRemove(body) {
  return fetch('/v3/fiber/transactions/remove-document', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fiberIncomingTransactionTracingGet(id) {
  return fetch(`/v3/fiber/transactions/incoming/${id}/tracing`, {
    method: 'GET',
  });
}

export function fiberOutgoingTransactionTracingGet(id) {
  return fetch(`/v3/fiber/transactions/outgoing/${id}/tracing`, {
    method: 'GET',
  });
}

export function fiberLotTracingGet(id) {
  return fetch(`/v3/fiber/lots/${id}/tracing`, {
    method: 'GET',
  });
}

export function fiberLinkTransactionsDetailsGet({ ids }) {
  return fetch(
    `/v3/fiber/transactions/transactions-by-link-ids?${getDefinedUrlSearchParams(
      {
        ids,
      },
    )}`,
    {
      method: 'GET',
    },
  );
}

export function fiberNodeDetailGet({ ids }) {
  return fetch(
    `/v3/fiber/transactions/nodes?${getDefinedUrlSearchParams({
      ids,
    })}`,
    {
      method: 'GET',
    },
  );
}

export function fiberSettingsGet() {
  return fetch('/v3/fiber/settings', {
    method: 'GET',
  });
}

export function createBulkLots(body) {
  return fetch('/v3/fiber/lots/bulk-create', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getAdminDocumentTypesV2(queryParams) {
  return fetch(
    [
      `${getApiOrigin()}/api/admin/document-types?${getDefinedUrlSearchParams(queryParams)}`,
    ],
    {
      method: 'GET',
    },
  );
}

export function createAdminDocumentTypeV2(body) {
  return fetch([`${getApiOrigin()}/api/admin/document-types`], {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateDocumentTypeUsage({ id, deprecate }) {
  return fetch([`${getApiOrigin()}/api/admin/document-types/${id}/usage`], {
    method: 'PATCH',
    body: JSON.stringify({ deprecate }),
  });
}

export function deleteDocumentType({ id }) {
  return fetch([`${getApiOrigin()}/api/admin/document-types/${id}`], {
    method: 'DELETE',
  });
}

export function patchDocumentType(id, body) {
  return fetch([`${getApiOrigin()}/api/admin/document-types/${id}`], {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function supplyChainExtend(id, body) {
  return fetch(`/v3/supply-chains/${id}/extend`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function supplyChainMappingGet(id, queryParams) {
  return fetch(
    `/v3/supply-chains/${id}?${getDefinedUrlSearchParams(queryParams)}`,
    {},
  );
}

export function supplyChainCompanyMappingGet(id, _queryParams) {
  return fetch(`/v3/supply-chains/${id}`, {
    method: 'GET',
  });
}

export function supplyChainMappingsCreate(body) {
  return fetch('/v3/supply-chains', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function supplyChainMappingsGet(queryParams) {
  return fetch(`/v3/supply-chains?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function supplyChainLinkDetailsGet(id, queryParams) {
  return fetch(
    `/v3/supply-chains/link/${id}?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

export function supplyChainLinkDetailUpdate(id, body) {
  return fetch(`/v3/supply-chains/links/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function supplyChainMappingNodeDelete(id, queryParams) {
  return fetch(
    `/v3/supply-chains/nodes/${id}?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'DELETE',
    },
  );
}

export function productOrdersGet(id) {
  return fetch(`/v3/products/${id}/traced-orders`, {
    method: 'GET',
  });
}

export function supplyChainsBulkArchive(supplyChainIds) {
  return fetch('/v3/supply-chains/archive', {
    method: 'POST',
    body: JSON.stringify(supplyChainIds),
  });
}

export function copySupplyChainMapping(body) {
  return fetch('/v3/supply-chains/copy', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function supplyChainFacilityProcessUpdate(nodeId, body) {
  return fetch(`/v3/supply-chains/nodes/${nodeId}/featured-facility-process`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function markCompaniesClient(body) {
  return fetch('/v3/admin/clients', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function removeCompaniesClient(body) {
  return fetch('/v3/admin/clients', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export function getAttachmentTemplatesByOrderIds(type, queryParams) {
  return fetch(
    [
      `${getApiOrigin()}/api/attachment-templates/documents?${getDefinedUrlSearchParams({ ...queryParams, tracingType: type })}`,
    ],
    {
      method: 'GET',
    },
  );
}
